import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/** What a copy just saved, stamped so a newer copy's tick outlives an older one's clear. */
export interface CopiedMarks {
  paddockIds: string[];
  inputIds: string[];
  stamp: number;
}

export interface CopyToolState {
  isOpen: boolean;
  // Id to stamp of the paddocks and documents a copy just saved, ticked in the tree for a moment.
  copiedPaddocks: Record<string, number>;
  copiedInputs: Record<string, number>;
  // The documents the running copy still has to write; Planning holds the open one until then.
  copyingInputs: Record<string, true>;
  // A copy (or a Retry) is running; one at a time, so a copy never reads what another is writing.
  isCopyRunning: boolean;
}

const initialState: CopyToolState = {
  isOpen: false,
  copiedPaddocks: {},
  copiedInputs: {},
  copyingInputs: {},
  isCopyRunning: false,
};

const copyToolSlice = createSlice({
  name: 'copyTool',
  initialState,
  reducers: {
    openCopyTool: (state) => {
      state.isOpen = true;
    },
    // A copy keeps running after the tool closes, so its ticks stay.
    closeCopyTool: (state) => {
      state.isOpen = false;
    },
    markCopied: (state, action: PayloadAction<CopiedMarks>) => {
      const { paddockIds, inputIds, stamp } = action.payload;
      paddockIds.forEach((id) => {
        state.copiedPaddocks[id] = stamp;
      });
      inputIds.forEach((id) => {
        state.copiedInputs[id] = stamp;
      });
    },
    clearAllCopied: (state) => {
      state.copiedPaddocks = {};
      state.copiedInputs = {};
    },
    copyStarted: (state) => {
      state.isCopyRunning = true;
    },
    copyFinished: (state) => {
      state.isCopyRunning = false;
    },
    startCopyingInputs: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        state.copyingInputs[id] = true;
      });
    },
    finishCopyingInput: (state, action: PayloadAction<string>) => {
      delete state.copyingInputs[action.payload];
    },
    clearCopied: (state, action: PayloadAction<CopiedMarks>) => {
      const { paddockIds, inputIds, stamp } = action.payload;
      paddockIds.forEach((id) => {
        if (state.copiedPaddocks[id] === stamp) delete state.copiedPaddocks[id];
      });
      inputIds.forEach((id) => {
        if (state.copiedInputs[id] === stamp) delete state.copiedInputs[id];
      });
    },
  },
});

export const {
  openCopyTool,
  closeCopyTool,
  markCopied,
  clearCopied,
  clearAllCopied,
  startCopyingInputs,
  finishCopyingInput,
  copyStarted,
  copyFinished,
} = copyToolSlice.actions;

export default copyToolSlice.reducer;
